/**
 * The "Apply to past transactions" window. Mirrors the server's clamp
 * (backend #439): max(backfill_start_date ?? today - 90d, today - 365d), in
 * exact calendar days. `today` is the caller's LOCAL ISO date — never read
 * here, so the dry-run count and the rows written describe the same window.
 * All arithmetic is on YYYY-MM-DD parts via UTC, so no timezone can shift it.
 */
export const DEFAULT_DAYS = 90
export const MAX_DAYS = 365

const DAY_MS = 86_400_000
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/

/** ms since epoch for a valid ISO date, else null (rejects 2026-02-30 too). */
function parse(iso: string): number | null {
  const m = ISO.exec(iso)
  if (!m) return null
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const ms = Date.UTC(y, mo - 1, d)
  const back = new Date(ms)
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) {
    return null
  }
  return ms
}

function format(ms: number): string {
  const dt = new Date(ms)
  const p = (n: number, w: number) => String(n).padStart(w, '0')
  return `${p(dt.getUTCFullYear(), 4)}-${p(dt.getUTCMonth() + 1, 2)}-${p(dt.getUTCDate(), 2)}`
}

function minusDays(today: string, days: number): string {
  const ms = parse(today)
  if (ms === null) throw new RangeError(`today must be YYYY-MM-DD, got "${today}"`)
  return format(ms - days * DAY_MS)
}

/** `min` for the date input. */
export function earliestAllowed(today: string): string {
  return minusDays(today, MAX_DAYS)
}

/**
 * What reaches the wire as backfill_start_date. null stays null; too-old is
 * clamped to the floor; a future date becomes today. An empty or unparseable
 * string is treated as no choice (null) rather than guessed at.
 */
export function clampStart(chosen: string | null, today: string): string | null {
  if (chosen === null || parse(chosen) === null) return null
  const floor = earliestAllowed(today)
  if (chosen < floor) return floor // ISO dates order lexicographically
  if (chosen > today) return today
  return chosen
}

/** The first day the backfill covers, matching the server's own clamp. */
export function windowStart(chosen: string | null, today: string): string {
  return clampStart(chosen, today) ?? minusDays(today, DEFAULT_DAYS)
}

/** The user's LOCAL calendar date as YYYY-MM-DD — the `today` every function
 *  above takes. Never `toISOString()`, which is the UTC date and is a day off
 *  for part of every day west or east of Greenwich. */
export function localToday(now: Date = new Date()): string {
  const p = (n: number, w: number) => String(n).padStart(w, '0')
  return `${p(now.getFullYear(), 4)}-${p(now.getMonth() + 1, 2)}-${p(now.getDate(), 2)}`
}

/** "2026-07-01" → "Jul 1, 2026". Read as UTC so no zone shifts the day. */
export function longDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "2026-09-05" → "Sep 5". Split, never parsed through a zone. */
export function shortDate(iso: string): string {
  const [, m, d] = iso.split('-')
  const month = MONTHS[Number(m) - 1]
  return month === undefined || !d ? iso : `${month} ${Number(d)}`
}
